import React from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Mail, CheckSquare, Bell, Calendar, ChevronRight, AlertTriangle, Target, Plug, RefreshCw } from "lucide-react";
import { getDashboard, getNextAction } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { PageHeader, LoadingCards, ErrorState, EmptyState, RefreshNotice } from "@/components/Common";
import { PRIORITY, TASK_STATUS, fiTime } from "@/lib/constants";
import { Progress } from "@/components/ui/progress";
import { DaySituation } from "@/components/DaySituation";
import {EmailTaskReviewNotice} from "@/components/EmailTaskReviewNotice";
import QuickTaskCapture from "@/components/QuickTaskCapture";
import {AssistantWorkspace} from "@/components/AssistantWorkspace";

const StatCard = ({ to, icon: Icon, count, label, tone, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="rounded-xl border border-border p-3 flex items-center gap-2 hover:border-primary/40 transition-colors"
  >
    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="font-heading text-lg font-bold text-foreground leading-none" aria-label={count==null?"Määrää ei voitu varmistaa":undefined}>{count??"—"}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  </Link>
);

export default function Home() {
  const { data, loading, error, refetch, refreshing, updatedAt } = useData(getDashboard, [], ["calendar", "tasks", "emails", "reminders"],{autoRefreshMs:60000});
  const {
    data: nextAction,
    loading: nextActionLoading,
    error: nextActionError,
    refreshing:nextActionRefreshing,
    refetch: refreshNextAction,
  } = useData(getNextAction, [], ["calendar", "tasks", "emails", "reminders", "collaboration"],{autoRefreshMs:60000});

  if (loading) return <><PageHeader icon={LayoutDashboard} title="Yhteenveto" /><LoadingCards count={3} /></>;
  if (error && !data) return <ErrorState error={error} onRetry={refetch} />;

  const c = data.counts;
  const hasCounts = Object.values(c).some((value) => value > 0);
  const unknownCounts=["urgent_emails","reply_needed","active_tasks","reminders"].some(key=>c[key]==null);
  const hasContent = hasCounts || data.today_events.length > 0 || data.urgent_emails.length > 0 || data.objectives?.length > 0 || data.active_tasks.length > 0;
  return (
    <div className="space-y-6">
      <RefreshNotice refreshing={refreshing} error={data ? error : null} updatedAt={updatedAt} onRetry={refetch} />
      <QuickTaskCapture/>
      <AssistantWorkspace urgentEmails={data.urgent_emails} receiptState={data.solution_receipts}>
        <div className="grid grid-cols-2 gap-2.5" data-testid="home-stats">
          <StatCard to="/inbox?category=urgent" icon={Mail} count={c.urgent_emails} label="Kiireellistä viestiä" tone="bg-rose-500/15 text-rose-400" testid="stat-urgent" />
          <StatCard to="/inbox?category=reply_needed" icon={Mail} count={c.reply_needed} label="Vastausta vaativaa" tone="bg-sky-500/15 text-sky-400" testid="stat-reply" />
        </div>
        {unknownCounts&&<p role="status" className="text-sm text-amber-400">Kaikkia kokonaismääriä ei voitu varmistaa. Tuntematon määrä näkyy viivana, ei nollana.</p>}
        {data.email_coverage&&!data.email_coverage.arrival?.ready&&(!data.email_coverage.sync_complete||data.email_coverage.sync_stale||data.email_coverage.sync_error)&&<Link to="/inbox" className="inline-flex min-h-11 items-center text-sm text-amber-300">Sähköpostien ajantasaisuus on tarkistettava</Link>}
      </AssistantWorkspace>
      <details className="rounded-2xl border border-border p-4">
      <summary className="min-h-11 cursor-pointer font-semibold">Päivän suunnitelma ja taustatiedot</summary>
      <div className="mt-4 space-y-6">
      <div className="grid grid-cols-2 gap-2.5">
        <StatCard to="/tasks" icon={CheckSquare} count={c.active_tasks} label="Avointa tehtävää" tone="bg-primary/15 text-primary" testid="stat-tasks" />
        <StatCard to="/reminders" icon={Bell} count={c.reminders} label="Muistutusta" tone="bg-amber-500/15 text-amber-400" testid="stat-reminders" />
      </div>
      <section
        data-testid="next-action-card"
        aria-live="polite"
        aria-busy={nextActionLoading||nextActionRefreshing}
        className="rounded-2xl border border-primary/35 bg-primary/5 p-4 fade-up"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-primary">
            <h2 className="font-heading font-bold text-foreground">Mitä teen nyt?</h2>
          </div>
          <button
            type="button"
            data-testid="next-action-refresh"
            onClick={refreshNextAction}
            disabled={nextActionLoading||nextActionRefreshing}
            className="min-h-11 min-w-11 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:text-primary disabled:opacity-40"
            aria-label="Päivitä suositus"
          >
            <RefreshCw className={`h-4 w-4 ${nextActionLoading||nextActionRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        {nextActionLoading ? (
          <div className="mt-3 h-16 rounded-xl bg-muted/40 animate-pulse" />
        ) : nextActionError&&!nextAction ? (
          <p className="mt-3 text-sm text-muted-foreground">Suositusta ei saatu juuri nyt. Yritä päivittää uudelleen.</p>
        ) : nextAction ? (
          <div className="mt-3">
            {nextActionError&&<p role="alert" className="mb-2 text-sm text-amber-400">Suosituksen päivitys epäonnistui. Alla näkyy aiemmin haettu tieto.</p>}
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-heading text-lg font-semibold text-foreground">{nextAction.headline}</p>
              {nextAction.suggested_minutes ? (
                <span className="rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  {nextAction.suggested_minutes} min
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{nextAction.message}</p>
            <EmailTaskReviewNotice review={nextAction.email_task_review}/>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-[11px] text-muted-foreground">Perustuu tallennettuihin tietoihin</span>
              <Link data-testid="next-action-open" to={nextAction.target_path || "/tasks"} className="inline-flex min-h-11 items-center text-sm font-semibold text-primary">
                {nextAction.status==="review_email_task"?"Avaa ehdotus":(nextAction.target_path?.includes("?") ? {"/tasks":"Avaa tehtävä","/calendar":"Avaa tapahtuma","/reminders":"Avaa muistutus","/inbox":"Avaa viesti"} : {"/tasks":"Avaa tehtävät","/calendar":"Avaa kalenteri","/reminders":"Avaa muistutukset","/inbox":"Avaa sähköpostit"})[nextAction.target_path?.split("?")[0]] || "Avaa tiedot"} <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ) : null}
      </section>

      <DaySituation />

      {data.email_coverage && <section aria-label="Sähköpostien kattavuus" className="rounded-2xl border border-border p-4 text-sm">
        <p>{data.email_coverage.checked} / {data.email_coverage.total} haettua sähköpostia on luokiteltu luotettavasti.</p>
        {data.email_coverage.needs_review > 0 && <Link className="inline-flex min-h-11 items-center text-primary" to="/inbox?category=needs_review">{data.email_coverage.needs_review} viestiä odottaa tarkistusta</Link>}
        {data.email_coverage.arrival?.ready?<p>Uudet Gmail-viestit saapuvat automaattisesti. Aloitustuonti kattaa enintään 100 uusinta viestiä; vanha arkisto voi sisältää lisää.</p>:(!data.email_coverage.sync_complete || data.email_coverage.sync_stale || data.email_coverage.sync_error) && <p className="text-amber-300">Sähköpostien haun ajantasaisuutta tai kattavuutta ei ole vahvistettu. Nolla kiireellistä ei tarkoita, ettei postissa olisi tärkeää asiaa.</p>}
      </section>}

      {!unknownCounts && !hasContent && !nextActionLoading && (!nextAction || nextActionError) && (
        <EmptyState icon={Plug} title="Ei näytettäviä tietoja" hint="Tälle päivälle ei ole tallennettuja tapahtumia tai avoimia kohteita. Tarkista tietojen päivitysaika ja yhteyksien tila niiden omista näkymistä." />
      )}

      {/* Today's calendar */}
      {data.today_events.length > 0 && <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-foreground flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Tänään</h2>
          <Link to="/calendar" data-testid="home-calendar-link" className="text-xs text-primary flex items-center">Kalenteri <ChevronRight className="h-3 w-3" /></Link>
        </div>
          <div className="space-y-2" data-testid="home-events">
            {data.today_events.map((e) => (
              <Link key={e.id} to={`/calendar?event=${encodeURIComponent(e.id)}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 fade-up">
                <div className="text-center min-w-[48px]">
                  <p className="font-mono text-sm font-semibold text-primary">{fiTime(e.start)}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{fiTime(e.end)}</p>
                </div>
                <div className="h-10 w-px bg-border" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{e.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.location}</p>
                </div>
                {e.conflict && <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" title="Päällekkäisyys" />}
              </Link>
            ))}
          </div>
      </section>}

      {/* Urgent emails */}
      {(data.urgent_emails.length > 0||c.urgent_emails>0) && <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-foreground flex items-center gap-2"><Mail className="h-4 w-4 text-rose-400" /> Kiireelliset sähköpostit</h2>
          <Link to="/inbox?category=urgent" data-testid="home-inbox-link" className="text-xs text-primary flex items-center">Kaikki <ChevronRight className="h-3 w-3" /></Link>
        </div>
        {c.urgent_emails>data.urgent_emails.length&&<p className="text-xs text-muted-foreground">Näytetään {data.urgent_emails.length} / {c.urgent_emails} kiireelliseksi luokiteltua viestiä. Avaa koko jono Postista.</p>}
          <div className="space-y-2" data-testid="home-urgent-emails">
            {data.urgent_emails.map((e) => (
              <Link key={e.id} to={`/inbox?email=${encodeURIComponent(e.id)}`} className="block rounded-2xl border border-rose-500/25 bg-rose-500/5 p-3 fade-up">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-foreground truncate">{e.subject}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{e.sender_name}</p>
              </Link>
            ))}
          </div>
      </section>}

      {/* Weekly objectives */}
      {data.objectives?.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading font-bold text-foreground flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Viikon tavoitteet</h2>
          <div className="space-y-3" data-testid="home-objectives">
            {data.objectives.map((o) => (
              <div key={o.id} className="rounded-2xl border border-border bg-card p-4 fade-up">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">{o.title}</p>
                  <span className="font-mono text-sm font-semibold text-primary">{o.progress}%</span>
                </div>
                <Progress value={o.progress} className="h-2" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top tasks */}
      {(data.active_tasks.length > 0||c.active_tasks>0) && <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-foreground flex items-center gap-2"><CheckSquare className="h-4 w-4 text-primary" /> Avoimet tehtävät</h2>
          <Link to="/tasks" data-testid="home-tasks-link" className="text-xs text-primary flex items-center">Kaikki <ChevronRight className="h-3 w-3" /></Link>
        </div>
        {c.active_tasks>Math.min(4,data.active_tasks.length)&&<p className="text-xs text-muted-foreground">Näytetään {Math.min(4,data.active_tasks.length)} / {c.active_tasks} avointa tehtävää. Loput löytyvät Tehtävät-näkymästä.</p>}
        <div className="space-y-2" data-testid="home-tasks">
          {data.active_tasks.slice(0, 4).map((t) => (
            <Link key={t.id} to={`/tasks?task=${encodeURIComponent(t.id)}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 fade-up">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{t.title}</p>
                <p className="text-xs text-muted-foreground">{TASK_STATUS[t.status]}</p>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${PRIORITY[t.priority].className}`}>
                {PRIORITY[t.priority].label}
              </span>
            </Link>
          ))}
        </div>
      </section>}
      </div>
      </details>
    </div>
  );
}
